<template>
    <div class="ak-page">
        <div class="ak-crumb">
            <router-link to="/">Dashboard</router-link>
            <span class="ak-crumb-sep">/</span>
            <span class="ak-crumb-current">API Keys</span>
        </div>

        <div class="ak-header">
            <div>
                <h1 class="ak-title">API Keys</h1>
                <p class="ak-subtitle">Give clients read-only access to tender data. Keys follow a plan; quotas count tender records returned.</p>
            </div>
            <button v-if="tab === 'keys'" class="ak-btn primary" @click="showCreate = true">+ New API Key</button>
        </div>

        <div class="ak-tabs" role="tablist">
            <button role="tab" :aria-selected="tab === 'keys'" :class="{ on: tab === 'keys' }" @click="tab = 'keys'">
                Keys <span class="ak-tab-count">{{ keys.length }}</span>
            </button>
            <button role="tab" :aria-selected="tab === 'plans'" :class="{ on: tab === 'plans' }" @click="tab = 'plans'">
                Plans <span class="ak-tab-count">{{ plans.length }}</span>
            </button>
        </div>

        <!-- Keys tab -->
        <div v-show="tab === 'keys'">
            <div class="ak-filters">
                <input class="ak-input ak-search" v-model="search" placeholder="Search name, client or key prefix">
                <select class="ak-input ak-status" v-model="statusFilter">
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="expired">Expired</option>
                    <option value="revoked">Revoked</option>
                </select>
            </div>

            <div v-if="loadingKeys" class="ak-empty">Loading keys...</div>
            <div v-else-if="filteredKeys.length" class="ak-table-wrap">
                <table class="ak-table">
                    <thead>
                        <tr>
                            <th>Key / Client</th>
                            <th>Prefix</th>
                            <th>Plan</th>
                            <th>Status</th>
                            <th>Last used</th>
                            <th>Expires</th>
                            <th>Today</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="k in filteredKeys" :key="k.id" class="ak-row-click" @click="detailId = k.id">
                            <td>
                                <div class="ak-cell-name">{{ k.name }}</div>
                                <div class="ak-cell-sub">{{ k.client_name }}</div>
                            </td>
                            <td><code class="ak-prefix">bsk_live_{{ k.key_prefix }}…</code></td>
                            <td>{{ k.plan_name || '—' }}<span v-if="hasOverrides(k)" class="ak-badge info ak-ovr">custom</span></td>
                            <td><span class="ak-badge" :class="statusClass(k.status)">{{ k.status }}</span></td>
                            <td>{{ fmtDateTime(k.last_used_at) }}</td>
                            <td>{{ k.expires_at ? fmtDate(k.expires_at) : 'Never' }}</td>
                            <td>
                                <span v-if="k.usage_today">{{ k.usage_today.tenders_served.toLocaleString('en-IN') }} tenders<br>
                                    <small>{{ k.usage_today.requests.toLocaleString('en-IN') }} requests</small></span>
                                <span v-else>—</span>
                            </td>
                            <td class="ak-cell-go" aria-hidden="true">›</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="ak-empty">
                <p>{{ keys.length ? 'No keys match your filters.' : 'No API keys yet.' }}</p>
                <button v-if="!keys.length" class="ak-btn ghost" @click="showCreate = true">Create the first key</button>
            </div>
        </div>

        <!-- Plans tab -->
        <ApiKeyPlans v-show="tab === 'plans'" :plans="plans" :portals="portals" @changed="onPlansChanged" @notify="notify" />

        <ApiKeyCreateModal v-if="showCreate" :plans="plans" :portals="portals" @close="showCreate = false" @created="onCreated" />
        <ApiKeyDetail v-if="detailId !== null" :key="detailId" :key-id="detailId" :plans="plans" :portals="portals"
            @close="detailId = null" @updated="fetchKeys" @reveal="onReveal" @notify="notify" />
        <ApiKeyReveal v-if="reveal" :reveal="reveal" @close="reveal = null" />

        <div v-if="toast.message" class="ak-toast">
            <div class="ak-toast-body" :class="toast.type">
                <span>{{ toast.message }}</span>
                <button type="button" class="ak-toast-x" aria-label="Dismiss" @click="toast.message = ''">×</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import ApiKeyPlans from './apiKeys/ApiKeyPlans.vue';
import ApiKeyCreateModal from './apiKeys/ApiKeyCreateModal.vue';
import ApiKeyDetail from './apiKeys/ApiKeyDetail.vue';
import ApiKeyReveal from './apiKeys/ApiKeyReveal.vue';
import { authHeaders, errorMessage, fmtDate, fmtDateTime } from './apiKeys/apiKeysCommon';

export default {
    name: 'ApiKeys',
    components: { ApiKeyPlans, ApiKeyCreateModal, ApiKeyDetail, ApiKeyReveal },
    data() {
        return {
            tab: 'keys',
            keys: [],
            plans: [],
            portals: [],
            loadingKeys: true,
            search: '',
            statusFilter: '',
            showCreate: false,
            detailId: null,
            reveal: null,
            toast: { type: 'success', message: '' },
            toastTimer: null,
        };
    },
    computed: {
        filteredKeys() {
            const q = this.search.trim().toLowerCase();
            return this.keys.filter(k => {
                if (this.statusFilter && k.status !== this.statusFilter) return false;
                if (!q) return true;
                return [k.name, k.client_name, k.key_prefix].some(v => (v || '').toLowerCase().includes(q));
            });
        },
    },
    async mounted() {
        await Promise.all([this.fetchKeys(), this.fetchPlans(), this.fetchPortals()]);
    },
    beforeUnmount() {
        clearTimeout(this.toastTimer);
    },
    methods: {
        fmtDate, fmtDateTime,
        statusClass(s) {
            return { active: 'ok', expired: 'warn', revoked: 'bad', suspended: 'warn' }[s] || 'off';
        },
        hasOverrides(k) {
            return k.overrides && Object.keys(k.overrides).length > 0;
        },
        notify({ type, message }) {
            this.toast = { type, message };
            clearTimeout(this.toastTimer);
            this.toastTimer = setTimeout(() => { this.toast.message = ''; }, type === 'error' ? 6000 : 3500);
        },
        async fetchKeys() {
            try {
                const res = await axios.get(`${this.$API_URL}/super_admin/api-keys`, { headers: authHeaders() });
                this.keys = res.data.keys || [];
            } catch (err) {
                this.notify({ type: 'error', message: errorMessage(err, 'Failed to load API keys') });
            } finally {
                this.loadingKeys = false;
            }
        },
        async fetchPlans() {
            try {
                const res = await axios.get(`${this.$API_URL}/super_admin/api-plans`, { headers: authHeaders() });
                this.plans = res.data.plans || [];
            } catch (err) {
                this.notify({ type: 'error', message: errorMessage(err, 'Failed to load API plans') });
            }
        },
        async fetchPortals() {
            try {
                const res = await axios.get(`${this.$API_URL}/portals`);
                this.portals = res.data.portals || [];
            } catch (err) {
                this.portals = [];
            }
        },
        async onPlansChanged() {
            await Promise.all([this.fetchPlans(), this.fetchKeys()]);
        },
        async onCreated(data) {
            this.showCreate = false;
            this.reveal = {
                title: 'API key created',
                name: data.key ? data.key.name : '',
                api_key: data.api_key,
                signing_secret: data.signing_secret || null,
            };
            await Promise.all([this.fetchKeys(), this.fetchPlans()]);
        },
        onReveal(data) {
            this.detailId = null;
            this.reveal = data;
        },
    },
};
</script>

<style scoped>
.ak-page { font-family: var(--font-sans); }
.ak-crumb { margin-bottom: 1.5rem; display: flex; align-items: center; gap: 8px; font-size: var(--fs-sm); }
.ak-crumb a { color: var(--text-secondary); text-decoration: none; font-weight: 500; }
.ak-crumb a:hover { color: #0071C5; }
.ak-crumb-sep { color: #cbd5e1; }
.ak-crumb-current { color: #0071C5; font-weight: 700; }
.ak-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem; margin-bottom: 1.5rem; }
.ak-title {
    margin: 0; font-family: var(--font-display); font-size: var(--fs-h1); line-height: var(--lh-h1);
    font-weight: var(--fw-h1); color: var(--text-primary); letter-spacing: -0.02em;
}
.ak-subtitle { color: var(--text-secondary); margin: var(--space-heading-body) 0 0; font-size: var(--fs-sm); max-width: 640px; }
.ak-tabs { display: flex; gap: 0.25rem; border-bottom: 1px solid #e2e8f0; margin-bottom: 1.5rem; }
.ak-tabs button {
    border: none; background: transparent; padding: 0.7rem 1.1rem; font-size: 0.9rem; font-weight: 700; color: var(--text-secondary);
    cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -1px; font-family: var(--font-sans);
}
.ak-tabs button.on { color: #0071C5; border-bottom-color: #0071C5; }
.ak-tab-count { margin-left: 6px; font-size: 0.7rem; background: #f1f5f9; padding: 2px 8px; border-radius: 20px; color: #64748b; }
.ak-filters { display: flex; gap: 0.75rem; margin-bottom: 1rem; flex-wrap: wrap; }
.ak-search { max-width: 340px; }
.ak-status { max-width: 180px; }
.ak-table-wrap { background: #fff; border: 1px solid #eef2f7; border-radius: 16px; overflow-x: auto; }
.ak-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.ak-table th {
    text-align: left; padding: 0.8rem 1rem; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em;
    color: var(--text-label); background: #f8fafc; border-bottom: 1px solid #e2e8f0; white-space: nowrap;
}
.ak-table td { padding: 0.85rem 1rem; border-bottom: 1px solid #f1f5f9; color: var(--text-primary); vertical-align: middle; }
.ak-table tr:last-child td { border-bottom: none; }
.ak-row-click { cursor: pointer; transition: background 0.15s; }
.ak-row-click:hover { background: #f8fafc; }
.ak-cell-name { font-weight: 700; }
.ak-cell-sub { font-size: 0.75rem; color: #94a3b8; }
.ak-prefix { font-size: 0.78rem; color: #475569; background: #f1f5f9; padding: 2px 6px; border-radius: 6px; }
.ak-ovr { margin-left: 6px; }
.ak-cell-go { color: #cbd5e1; font-size: 1.3rem; text-align: right; }
.ak-empty { padding: 4rem 2rem; text-align: center; color: #94a3b8; background: #fff; border: 1px dashed #e2e8f0; border-radius: 20px; }
.ak-empty p { margin: 0 0 1rem; font-size: var(--fs-body-lg); font-weight: 500; }
</style>

<!-- Shared look for the API Keys screens (modals, forms, buttons, badges). Prefixed ak- to stay isolated. -->
<style>
.ak-overlay {
    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.6); backdrop-filter: blur(4px); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 2rem; overflow-y: auto;
}
.ak-modal {
    background: #fff; width: 100%; max-width: 560px; border-radius: 20px; overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); animation: akSlideUp 0.3s ease-out; max-height: 92vh; display: flex; flex-direction: column;
}
.ak-modal.wide { max-width: 760px; }
.ak-modal.narrow { max-width: 420px; }
.ak-modal.ak-confirm { padding: 2rem; text-align: center; overflow-y: auto; }
.ak-confirm h3 { margin: 0 0 0.6rem; font-size: 1.3rem; font-weight: 700; color: var(--text-primary); }
.ak-confirm p { color: var(--text-secondary); font-size: 0.92rem; line-height: 1.5; margin: 0 0 1.25rem; }
.ak-confirm .ak-label.left { text-align: left; }
.ak-modal-head {
    background: #f8fafc; padding: 1.25rem 1.75rem; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; gap: 1rem; flex-shrink: 0;
}
.ak-modal-head h2 { margin: 0; font-size: var(--fs-h3); font-weight: 800; color: var(--text-primary); }
.ak-modal-head p { margin: 0; font-size: 0.8rem; color: var(--text-secondary); font-weight: 500; }
.ak-modal-icon {
    background: #0071C5; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
    color: #fff; flex-shrink: 0;
}
.ak-modal-body { padding: 1.75rem; overflow-y: auto; display: flex; flex-direction: column; gap: 1.25rem; }
.ak-form { display: flex; flex-direction: column; gap: 1.25rem; }
.ak-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.ak-label {
    font-size: var(--fs-label); font-weight: var(--fw-label); letter-spacing: var(--tracking-label); text-transform: uppercase;
    color: var(--text-label); margin-bottom: 0.4rem; display: block;
}
.ak-input {
    width: 100%; padding: 0.75rem 1rem; border: 1.5px solid #e2e8f0; border-radius: 12px; font-size: 0.92rem;
    font-family: var(--font-sans); outline: none; transition: all 0.2s; box-sizing: border-box; background: #fff;
}
.ak-input:focus { border-color: #0071C5; box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1); }
.ak-input:disabled { background: #f8fafc; color: #94a3b8; }
textarea.ak-input { resize: vertical; }
.ak-hint { margin: 0.4rem 0 0; font-size: 0.75rem; color: #94a3b8; font-weight: 500; }
.ak-check { display: flex; align-items: center; gap: 8px; font-size: 0.85rem; font-weight: 600; color: var(--text-primary); cursor: pointer; }
.ak-check input { accent-color: #0071C5; width: 16px; height: 16px; flex-shrink: 0; }
.ak-error {
    background: #fef2f2; color: #ef4444; padding: 0.75rem; border-radius: 8px; font-size: 0.8rem; font-weight: 600; text-align: center;
    border: 1px solid #fee2e2;
}
.ak-error.left { text-align: left; }
.ak-warn {
    background: #fffbeb; color: #92400e; padding: 0.85rem 1rem; border-radius: 10px; font-size: 0.82rem; font-weight: 600;
    border: 1px solid #fde68a; line-height: 1.45;
}
.ak-actions { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 0.25rem; }
.ak-btn {
    padding: 0.7rem 1.3rem; border-radius: 12px; border: 1px solid transparent; font-weight: 700; font-size: 0.85rem;
    font-family: var(--font-sans); cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}
.ak-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.ak-btn.sm { padding: 0.4rem 0.8rem; font-size: 0.78rem; border-radius: 9px; }
.ak-btn.primary { background: #0071C5; color: #fff; box-shadow: 0 4px 12px rgba(0, 113, 197, 0.2); }
.ak-btn.primary:hover:not(:disabled) { background: #005A9E; }
.ak-btn.ghost { background: #fff; border-color: #e2e8f0; color: var(--text-secondary); }
.ak-btn.ghost:hover:not(:disabled) { background: #f8fafc; border-color: #0071C5; color: #0071C5; }
.ak-btn.ghost.danger-text:hover:not(:disabled) { border-color: #ef4444; color: #ef4444; }
.ak-btn.danger { background: #ef4444; color: #fff; }
.ak-btn.danger:hover:not(:disabled) { background: #dc2626; }
.ak-badge {
    display: inline-block; font-size: 0.68rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em;
    padding: 3px 10px; border-radius: 20px; background: #f1f5f9; color: #64748b; margin: 1px 4px 1px 0;
}
.ak-badge.ok { background: rgba(16, 185, 129, 0.12); color: #059669; }
.ak-badge.warn { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.ak-badge.bad { background: rgba(239, 68, 68, 0.1); color: #dc2626; }
.ak-badge.info { background: rgba(0, 113, 197, 0.08); color: #0071C5; text-transform: none; letter-spacing: 0; font-weight: 700; }
.ak-badge.off { background: #f1f5f9; color: #94a3b8; }
.ak-spinner {
    width: 16px; height: 16px; border: 2px solid rgba(255, 255, 255, 0.35); border-top-color: #fff; border-radius: 50%;
    animation: akSpin 0.8s linear infinite;
}
.ak-toast { position: fixed; top: 24px; left: 50%; transform: translateX(-50%); z-index: 2000; }
.ak-toast-body {
    background: #10b981; color: #fff; padding: 12px 20px; border-radius: 12px; display: flex; align-items: center; gap: 12px;
    font-weight: 700; box-shadow: 0 10px 25px -5px rgba(16, 185, 129, 0.4); animation: akToast 0.3s ease-out; max-width: 90vw;
}
.ak-toast-body.error { background: #ef4444; box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.4); }
.ak-toast-x { background: none; border: none; color: #fff; font-size: 1.2rem; line-height: 1; cursor: pointer; opacity: 0.85; }
@keyframes akSlideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes akSpin { to { transform: rotate(360deg); } }
@keyframes akToast { from { transform: translateY(-40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@media (max-width: 640px) {
    .ak-row { grid-template-columns: 1fr; }
    .ak-overlay { padding: 1rem; }
}
</style>
